import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X as XIcon, Plus, Trash2 } from 'lucide-react';
import { api } from '@/api/client';

interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B'; serviceType: string }
interface State { id: number; name: string }

const ZONES = ['N1', 'N2', 'E', 'NE', 'W1', 'W2', 'S1', 'S2', 'Central'] as const;

const OTHER_CHARGES = [
  { key: 'min_lr_charge', label: 'min_lr_charge', calc: 'Fixed', defaultMin: 450 },
  { key: 'min_charge_weight', label: 'min_charge_weight', calc: 'Fixed', defaultMin: 'NA' },
  { key: 'processing_charge', label: 'processing_charge', calc: 'Fixed Charge per LR', placeholder: 'Enter Amt.', defaultMin: 'NA' },
  { key: 'divisor', label: 'divisor', calc: 'for divide', defaultMin: 4000, defaultUnit: 4000 },
  { key: 'pod_charge', label: 'pod_charge', calc: 'Fixed Amt', placeholder: 'Enter Amt', defaultMin: 'NA' },
  { key: 'fuel_charge_percent', label: 'fuel_charge_percent', calc: '%', defaultMin: 'NA' },
  { key: 'rov_carrier', label: 'rov_carrier', calc: '% of Invoice Value or Min', defaultMin: 50 },
  { key: 'rov_owner', label: 'rov_owner', calc: '% of Invoice Value or Min', defaultMin: 100 },
  { key: 'oda_charge_amt', label: 'oda_charge_amt', calc: 'Min 600 if above unit charge is perkg', defaultMin: 600 },
  { key: 'cod_charge_amt', label: 'cod_charge_amt', calc: 'Fixed COD Charges', defaultMin: 'NA' },
  { key: 'other_handling_charge_amt', label: 'other_handling_charge_amt', calc: 'Min   KG', defaultMin: 600 },
  { key: 'green_tax_amt', label: 'green_tax_amt', calc: 'Fixed 200', defaultMin: 'NA', defaultUnit: 200 },
  { key: 'demurrage_charge_amt', label: 'demurrage_charge_amt', calc: 'Fixed or per day', defaultMin: 200 },
  { key: 'cheque_charge_amt', label: 'cheque_charge_amt', calc: 'Fixed', defaultMin: 'NA', defaultUnit: 100 },
  { key: 'fm_charges', label: 'fm_charges', calc: 'Fixed or per_kg', defaultMin: 200 },
  { key: 'to_pay_handling_charges', label: 'to_pay_handling_charges', calc: 'Fixed 250', defaultMin: 'NA', defaultUnit: 250 },
];

interface Props {
  merchantId: number;
  planId?: number;
  defaultValues?: any;
  onSuccess: () => void;
}

export function B2bPricingForm({ merchantId, planId, defaultValues, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!planId;
  const [courierIds, setCourierIds] = useState<number[]>(defaultValues?.courierIds ?? []);
  const [pickerOpen, setPickerOpen] = useState(false);

  const cfg0 = defaultValues?.config ?? {};
  const [zoneMatrix, setZoneMatrix] = useState<Record<string, Record<string, number | undefined>>>(
    cfg0.zoneMatrix ?? {}
  );
  const [otherCharges, setOtherCharges] = useState<Record<string, { unit?: number | string; min?: number | string }>>(
    cfg0.otherCharges ?? Object.fromEntries(OTHER_CHARGES.map(o => [o.key, { unit: o.defaultUnit, min: o.defaultMin }]))
  );
  const [stateCharges, setStateCharges] = useState<{ stateId?: number; charge?: number }[]>(
    cfg0.stateCharges?.length ? cfg0.stateCharges : [{}]
  );
  // City rates beat state rates, which beat the zone matrix — see the precedence note in the form.
  const [cityCharges, setCityCharges] = useState<{ cityId?: number; cityName?: string; charge?: number }[]>(
    cfg0.cityCharges?.length ? cfg0.cityCharges : []
  );

  // Show B2B couriers — prefer merchant-assigned, fall back to all B2B if none assigned
  const { data: assignedMcs } = useQuery({
    queryKey: ['merchant-couriers-b2b', merchantId],
    queryFn: async () => {
      const mcs = (await api.get<any[]>(`/merchants/${merchantId}/couriers`)).data;
      return (mcs ?? []).filter((mc: any) => mc.isActive && mc.courier?.orderType === 'B2B').map((mc: any) => mc.courier as Courier);
    },
  });
  const { data: allB2bCouriers } = useQuery({
    queryKey: ['couriers', 'B2B', 'active'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { orderType: 'B2B', isActive: 'true' } })).data,
  });
  // De-duplicate the courier list by id so the picker never shows the same courier twice.
  const couriersRaw = (assignedMcs && assignedMcs.length > 0) ? assignedMcs : (allB2bCouriers ?? []);
  const couriers = Array.from(new Map(couriersRaw.map(c => [c.id, c])).values());
  const noneAssigned = !assignedMcs || assignedMcs.length === 0;
  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });

  const save = useMutation({
    mutationFn: async () => {
      const uniqueCourierIds = Array.from(new Set(courierIds));
      const config = {
        zoneMatrix,
        otherCharges,
        stateCharges: stateCharges.filter(s => s.stateId && s.charge != null),
        cityCharges: cityCharges.filter(c => c.cityId && c.charge != null),
      };
      if (!anyPriceOver1({ zoneMatrix, stateCharges: config.stateCharges, cityCharges: config.cityCharges })) {
        throw new Error('Please enter at least one zone / state / city rate greater than ₹1 before saving.');
      }
      const payload = { serviceType: 'B2B', courierIds: uniqueCourierIds, config, isActive: true };
      if (isEdit) return (await api.patch(`/merchants/${merchantId}/pricing/${planId}`, payload)).data;
      return (await api.post(`/merchants/${merchantId}/pricing`, payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-pricing', merchantId] }); toast.success(isEdit ? 'Pricing updated' : 'Pricing created'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Save failed'),
  });

  const togglePick = (id: number) => setCourierIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const courierById = new Map((couriers ?? []).map(c => [c.id, c]));

  const setMatrixCell = (origin: string, dest: string, value: string) => {
    setZoneMatrix(m => ({ ...m, [origin]: { ...(m[origin] ?? {}), [dest]: value === '' ? undefined : Number(value) } }));
  };

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-600 mb-1">Select Service Type</label>
          <div className="input flex items-center gap-2"><Chip label="B2B" /><span className="text-xs text-slate-400">B2B plan</span></div>
        </div>
        <div className="flex-1 relative">
          <label className="block text-xs font-medium text-slate-600 mb-1">Select Couriers</label>
          <div className="input flex items-center gap-1 flex-wrap min-h-[42px] cursor-pointer" onClick={() => setPickerOpen(o => !o)}>
            {noneAssigned && couriers.length > 0 && (
            <div className="text-xs text-amber-600 mb-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded">No couriers are assigned to this merchant yet — showing all B2B couriers. Assign them in the Couriers tab to restrict the list.</div>
          )}
          {courierIds.length === 0
              ? <span className="text-slate-400 text-sm">Choose Couriers</span>
              : courierIds.map(id => {
                  const c = courierById.get(id);
                  return <Chip key={id} label={`${id} - ${c?.name ?? `#${id}`}`} onRemove={(e) => { e?.stopPropagation(); togglePick(id); }} />;
                })}
          </div>
          {pickerOpen && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {(couriers ?? []).length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">No B2B couriers available. Add B2B couriers in Couriers module first.</div>
              )}
              {(couriers ?? []).map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={courierIds.includes(c.id)} onChange={() => togglePick(c.id)} className="rounded" />
                  <span>{c.id} - {c.name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{c.serviceType}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Zone matrix */}
      <div className="border border-slate-200 rounded-lg p-3">
        <h4 className="text-sm font-semibold text-slate-900 mb-2">Zone</h4>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 text-slate-500">Ori/Dest</th>
                {ZONES.map(z => <th key={z} className="px-2 py-1 text-slate-500 text-center">{z}</th>)}
              </tr>
            </thead>
            <tbody>
              {ZONES.map(o => (
                <tr key={o}>
                  <td className="px-2 py-1 font-medium text-slate-700">{o}</td>
                  {ZONES.map(d => (
                    <td key={d} className="px-1 py-1">
                      <input type="number" step="0.01"
                        value={zoneMatrix[o]?.[d] ?? ''}
                        onChange={(e) => setMatrixCell(o, d, e.target.value)}
                        className="w-16 px-1.5 py-1 border border-slate-300 rounded text-sm text-center focus:border-primary outline-none" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Other Charges */}
      <div className="border border-slate-200 rounded-lg p-3">
        <h4 className="text-sm font-semibold text-slate-900 mb-3">Other Charges</h4>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-2 py-1.5">Other Charges (VAS)</th>
              <th className="text-left px-2 py-1.5">Calculation</th>
              <th className="text-left px-2 py-1.5">Unit Charge</th>
              <th className="text-left px-2 py-1.5">Min</th>
            </tr>
          </thead>
          <tbody>
            {OTHER_CHARGES.map((row) => (
              <tr key={row.key} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-mono text-xs">{row.label}</td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{row.calc}</td>
                <td className="px-2 py-1.5">
                  <input type="text" value={otherCharges[row.key]?.unit ?? ''}
                    onChange={(e) => setOtherCharges(c => ({ ...c, [row.key]: { ...(c[row.key] ?? {}), unit: e.target.value } }))}
                    placeholder={row.placeholder ?? ''}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none" />
                </td>
                <td className="px-2 py-1.5">
                  <input type="text" value={otherCharges[row.key]?.min ?? ''}
                    onChange={(e) => setOtherCharges(c => ({ ...c, [row.key]: { ...(c[row.key] ?? {}), min: e.target.value } }))}
                    placeholder="NA"
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* How the three rate tables relate. Without this the form reads as three independent
          surcharges, which is exactly how state charges were mis-entered before. */}
      <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 text-xs text-indigo-900">
        <span className="font-semibold">How the ₹/kg rate is chosen:</span>{' '}
        destination <b>City</b> rate → else destination <b>State</b> rate → else the <b>Zone</b> matrix above.
        The most specific match wins <b>instead of</b> the others — these are alternatives, not extra charges added on top.
        All three are <b>₹ per kg</b>. Leave a city or state out to let it fall through to the next rule.
      </div>

      {/* City-wise Charges */}
      <div className="border border-slate-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-slate-900">City Wise Charges <span className="font-normal text-slate-400">(₹/kg — highest priority)</span></h4>
          <span className="text-[11px] text-slate-400">{cityCharges.length} {cityCharges.length === 1 ? 'city' : 'cities'}</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Search the city master and pick as many as you need. If the destination pincode's city is listed here,
          this rate is used and the state / zone rate is ignored.
        </p>
        <CityChargePicker rows={cityCharges} onChange={setCityCharges} />
      </div>

      {/* State-wise Charges */}
      <div className="border border-slate-200 rounded-lg p-3">
        <h4 className="text-sm font-semibold text-slate-900 mb-1">State Wise Charges <span className="font-normal text-slate-400">(₹/kg — used when no city rate matches)</span></h4>
        <p className="text-[11px] text-slate-500 mb-2">
          Applies to every destination in the state. Enter the <b>per-kg rate</b> for the lane, the same kind of
          number as the zone matrix — not a flat add-on.
        </p>
        <div className="space-y-2">
          {stateCharges.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select value={row.stateId ?? ''}
                onChange={(e) => setStateCharges(arr => arr.map((r, i) => i === idx ? { ...r, stateId: e.target.value === '' ? undefined : Number(e.target.value) } : r))}
                className="input flex-1">
                <option value="">Select State</option>
                {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input type="number" step="0.01" value={row.charge ?? ''} placeholder="Charge"
                onChange={(e) => setStateCharges(arr => arr.map((r, i) => i === idx ? { ...r, charge: e.target.value === '' ? undefined : Number(e.target.value) } : r))}
                className="input flex-1" />
              {stateCharges.length > 1 && (
                <button type="button" onClick={() => setStateCharges(arr => arr.filter((_, i) => i !== idx))}
                  className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
              )}
              {idx === stateCharges.length - 1 && (
                <button type="button" onClick={() => setStateCharges(arr => [...arr, {}])}
                  className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded"><Plus size={14} /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 sticky bottom-0 bg-white pb-2">
        <button type="button" onClick={onSuccess} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending || courierIds.length === 0}
          className="px-4 py-2 bg-emerald-700 text-white rounded-lg font-medium hover:bg-emerald-800 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={16} className="animate-spin" />}
          SUBMIT
        </button>
      </div>
    </div>
  );
}

/**
 * Pick cities from the master and give each one a ₹/kg rate.
 *
 * The search runs SERVER-side (`/cities?search=`) rather than pulling the whole master into the
 * browser — there are tens of thousands of cities and a client-side filter would mean shipping all
 * of them to render one dropdown. Debounced so typing "ban" doesn't fire a request per keystroke.
 *
 * A city already in the list is shown as picked and re-selecting it is a no-op, so the same city
 * can't end up with two conflicting rates (the pricing lookup would silently take the first).
 */
function CityChargePicker({
  rows, onChange,
}: {
  rows: { cityId?: number; cityName?: string; charge?: number }[];
  onChange: (rows: { cityId?: number; cityName?: string; charge?: number }[]) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery({
    queryKey: ['cities-search', debounced],
    queryFn: async () => (await api.get<any>('/cities', { params: { search: debounced, pageSize: 25, isActive: 'true' } })).data,
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });
  const results: any[] = data?.items ?? [];
  const picked = new Set(rows.map(r => Number(r.cityId)));

  const add = (c: any) => {
    if (picked.has(Number(c.id))) return;
    onChange([...rows, { cityId: c.id, cityName: `${c.name}${c.state?.name ? `, ${c.state.name}` : ''}`, charge: undefined }]);
    setTerm(''); setDebounced('');
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Type at least 2 letters to search cities — e.g. Luck…"
          className="input w-full"
        />
        {open && debounced.length >= 2 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {isFetching && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
            {!isFetching && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No city matches “{debounced}”.</div>}
            {results.map((c: any) => (
              <button key={c.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(c)}
                disabled={picked.has(Number(c.id))}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                <span>{c.name}</span>
                <span className="text-xs text-slate-400">{c.state?.name ?? ''}</span>
                {picked.has(Number(c.id)) && <span className="ml-auto text-[10px] text-emerald-600">added</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No city rates — every destination falls through to the state or zone rate.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, idx) => (
            <div key={`${r.cityId}-${idx}`} className="flex items-center gap-2">
              <div className="input flex-1 bg-slate-50 text-slate-700 text-sm flex items-center">
                {r.cityName ?? `City #${r.cityId}`}
              </div>
              <input type="number" step="0.01" min="0" value={r.charge ?? ''} placeholder="₹ / kg"
                onChange={(e) => onChange(rows.map((x, i) => i === idx ? { ...x, charge: e.target.value === '' ? undefined : Number(e.target.value) } : x))}
                className="input w-32" />
              <button type="button" onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// True if the given config subset carries at least one price value greater than ₹1 anywhere.
function anyPriceOver1(obj: any): boolean {
  if (obj == null) return false;
  if (typeof obj === 'number') return obj > 1;
  if (typeof obj === 'string') { const n = Number(obj); return Number.isFinite(n) && n > 1; }
  if (Array.isArray(obj)) return obj.some(anyPriceOver1);
  if (typeof obj === 'object') return Object.values(obj).some(anyPriceOver1);
  return false;
}

function Chip({ label, onRemove }: { label: string; onRemove?: (e?: React.MouseEvent) => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
      {label}
      {onRemove && <button type="button" onClick={onRemove} className="hover:text-danger"><XIcon size={12} /></button>}
    </span>
  );
}
